import React, {lazy, Suspense, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import IconDown from '@assets/components/ResouceIcon/IconDown';
import FAQLauncher from '@assets/components/Launcher/FAQLauncher';
import {ICON_ONLY} from '@functions/const/floatLauncherButton';
import {
  addCampaignPopupShowed,
  addNewsletterPopupShowed,
  addWelcomePopupShowed,
  checkShowWelcomePopup
} from '@scripttag/helpers/storage';
import './FloatButtonWidget.scss';
import {getChatAttract} from '@scripttag/helpers/sessionHelpers';
import useBounceEffect from '@assets/hooks/widget/useBounceEffect';
import {i18nWidget} from '@functions/helpers/translation/i18n';

const NotificationMessage = lazy(() => import('@assets/components/Preview/NotificationMessage'));

/**
 * @param open
 * @param setOpen
 * @param {App.WidgetSetting} settings
 * @param translation
 * @param previewPage
 * @param selectedTab
 * @param isShowDesktop
 * @param setOpenModalWidget
 * @param setOpenWelcomePopup
 * @param isPreview
 * @param isLabelChanged
 * @param isAvailableTime
 * @param notificationMessage
 * @param emptyMessageNotification
 * @param clickButtonTrigger
 * @param setActiveCampaigns
 * @param activeCampaigns
 * @returns {Element}
 * @constructor
 */
export default function FloatButtonWidget({
  open,
  setOpen,
  settings,
  translation,
  previewPage,
  selectedTab,
  isShowDesktop,
  setOpenModalWidget,
  setOpenWelcomePopup,
  isPreview,
  isLabelChanged,
  isAvailableTime,
  notificationMessage = {},
  emptyMessageNotification = () => {},
  clickButtonTrigger = () => {},
  setActiveCampaigns = () => {},
  activeCampaigns = []
}) {
  const i18n = (phrase, variables) => {
    return i18nWidget({phrase, translation, variables, useEmailTag: false});
  };

  const [isNewsletterPopup, isChatAvail] = ['newsletter-popup', 'chat-availability'].map(
    x => x === previewPage
  );

  const isShowWelcomePopup = useMemo(() => !isPreview && checkShowWelcomePopup(), []);
  const {
    floatBtnPosition,
    floatBtnSize,
    isLiveChat,
    welcomePopupSetting,
    isEnableWelcomePopup,
    newsletterPopupSettings,
    isEnableNewsletterPopup
  } = settings;

  const floatButtonSizeClass = `button-${floatBtnSize.slice(0, 2)}`;
  const position = useMemo(() => floatBtnPosition.replace('_', ' '), [floatBtnPosition]);

  const isShowStatus = useMemo(() => {
    if (!isPreview) return isAvailableTime;
    if (isChatAvail) return selectedTab === 0;
    return true;
  }, [isAvailableTime, selectedTab]);

  const customClass = useMemo(() => !isShowDesktop && isPreview && 'admin mobile', [
    isShowDesktop,
    isPreview
  ]);

  const {isShowBounce, setCountClickFloatBtn} = useBounceEffect({open});
  const [isHovered, setIsHovered] = useState(false);
  const timeoutBounceRef = useRef(null);

  const buttonClass = [
    'Avada-Faqs_Button',
    position,
    open && 'opened',
    isShowBounce && 'bounce',
    `Avada-Faqs_Button--size-${floatBtnSize}`,
    customClass,
    isHovered && 'hover'
  ].filter(Boolean);

  const handleCloseModal = callback => {
    const modalSelector = isShowDesktop
      ? '.Avada-FAQ_Widget__Desktop .Avada-Faqs_Popup'
      : '.Avada-Faqs_Popup';

    const wrapperBlock = document.querySelector(modalSelector);
    if (!wrapperBlock) return callback();

    wrapperBlock.classList.remove('Avada-Faqs_Popup--Open');
    wrapperBlock.classList.add('Avada-Faqs_Popup--Close');
    setTimeout(() => callback(), 100);
  };

  const handleOpenWidget = () => {
    setOpenWelcomePopup(false);
    setCountClickFloatBtn(prev => prev + 1);
    if (timeoutBounceRef.current) clearTimeout(timeoutBounceRef.current);

    const callback = () => {
      setOpen(prev => !prev);
      clickButtonTrigger();

      if (isPreview) return setOpenModalWidget(prev => !prev);
      if (isShowWelcomePopup) addWelcomePopupShowed();
      if (isNewsletterPopup) addNewsletterPopupShowed();

      if (activeCampaigns?.length > 0) {
        addCampaignPopupShowed(activeCampaigns?.map(campaign => campaign.id));
        setActiveCampaigns([]);
      }
    };

    open ? handleCloseModal(callback) : callback();
  };

  const isChatAttract = getChatAttract();

  const activeTeaserCount = useMemo(() => {
    if (!isLiveChat) return 0;
    return [
      isEnableWelcomePopup && welcomePopupSetting.statusTeaser,
      isEnableNewsletterPopup && newsletterPopupSettings.statusTeaser
    ].filter(Boolean).length;
  }, [isLiveChat, settings]);

  const hasUnread = (() => {
    const {countUnread, messages = []} = notificationMessage || {};
    return countUnread > 0 || messages.length > 0;
  })();

  return (
    <div
      className={buttonClass.join(' ')}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {!isChatAttract && !!activeTeaserCount && (
        <div className={`Avada-MessagePopup-count ${floatButtonSizeClass}`}>
          {activeTeaserCount}
        </div>
      )}
      <Suspense fallback={<div />}>
        {!open && hasUnread && (
          <NotificationMessage {...{settings, notificationMessage, emptyMessageNotification}} />
        )}
      </Suspense>
      <button
        id="Avada-FAQ_WidgetTrigger"
        onClick={() => handleOpenWidget()}
        aria-label={i18n(open ? 'Close chatbox' : 'Open chatbox')}
        className={floatButtonSizeClass}
      >
        {open ? (
          <div
            style={{
              width: '100%',
              height: '100%',
              boxShadow: '0px 6px 12px 0px #00000040',
              borderRadius: '50%'
            }}
          >
            <IconDown color={settings.btnPopupColor} size={settings.floatBtnSize} />
          </div>
        ) : (
          <FAQLauncher
            {...{settings, i18n, isLabelChanged}}
            type={settings.floatBtnLauncher || ICON_ONLY}
            isCustomSize={true}
          />
        )}
        {isShowStatus && (
          <div className="Avada-Faqs_Button-Status" style={open ? {display: 'none'} : {}} />
        )}
      </button>
    </div>
  );
}

FloatButtonWidget.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  settings: PropTypes.object,
  translation: PropTypes.object,
  translations: PropTypes.object,
  previewPage: PropTypes.string,
  selectedTab: PropTypes.number,
  isShowDesktop: PropTypes.bool,
  setOpenModalWidget: PropTypes.func,
  setOpenWelcomePopup: PropTypes.func,
  isPreview: PropTypes.bool,
  isLabelChanged: PropTypes.bool,
  isAvailableTime: PropTypes.bool,
  notificationMessage: PropTypes.object,
  emptyMessageNotification: PropTypes.func,
  clickButtonTrigger: PropTypes.func,
  setActiveCampaigns: PropTypes.func,
  activeCampaigns: PropTypes.array
};
